import type { InputOnChangeData, ToastPosition } from "@fluentui/react-components";
import {
  Button,
  makeStyles,
  shorthands,
  Toaster,
  tokens,
  Tooltip,
  useId,
  SearchBox,
  useToastController,
} from "@fluentui/react-components";
import type { SearchBoxChangeEvent } from "@fluentui/react-components";
import { inject, observer } from "mobx-react";
import * as React from "react";
import Store from "./store";
import { useEffect } from "react";
import { toJS } from "mobx";
import { informUser } from "./utilities";

const useStyles = makeStyles({
  root: {
    // Stack the label above the field with a gap
    display: "grid",
    gridTemplateRows: "repeat(1fr)",
    justifyItems: "start",
    ...shorthands.gap("1em"),
  },
  tagsList: {
    listStyleType: "none",
    marginBottom: tokens.spacingVerticalXXS,
    marginTop: "0.5em",
    paddingLeft: 0,
    display: "flex",
    flexWrap: "wrap",
    gridGap: tokens.spacingHorizontalXXS,
  },
  searchIcon: {
    position: "absolute",
    left: "10px",
    top: "50%",
    transform: "translateY(-50%)", // Center the icon vertically
    zIndex: 1,
  },
  taxGuidanceCombobox: {
    width: "100%",
    "& .fui-Combobox-input": {
      paddingLeft: "2.5em", // Add padding for the icon
    },
    "& .fui-Combobox__input": {
      paddingLeft: "2.5em", // Add padding for the icon
    },
    "& .fui-Combobox__button": {
      display: "none", // Hide the dropdown chevron
    },
    "& .fui-Combobox__expandIcon": {
      display: "none", // Hide the expand icon
    },
    "& svg": {
      display: "none!important", // Hide the default chevron icon
    },
    "& .fui-Combobox-trigger": {
      display: "none!important", // Hide the dropdown chevron
      "& svg": {
        display: "none!important", // Hide the default chevron icon
      },
    },
    "& .fui-Combobox-inputWrapper": {
      position: "relative", // Needed for absolute positioning of the icon
    },
  },
  filterSection: {
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2), // Add border
    ...shorthands.padding("1rem"), // Add padding inside the box
    ...shorthands.borderRadius("4px"), // Optional: Add rounded corners
    marginBottom: "1rem", // Add margin below each section
  },
  tabList: {
    marginBottom: "1rem",
  },
  combobox: {
    width: "100%",
  },
  searchBar: {
    width: "100%",
    input: { paddingLeft: "5px" },
    callout: { display: "none" },
  },
  listbox: { maxHeight: "200px" },
  buttonArea: {
    display: "flex",
    flexDirection: "row",
    width: "95%",
  },
  documentList: {
    paddingLeft: 0,
    display: "block",
    textAlign: "left",
  },
  label: {
    fontSize: tokens.fontSizeBase200,
  },
});

const Pagination = ({ documentsPerPage, totalDocuments, setCurrentPage, currentPage }) => {
  const pageNumbers = [];

  for (let i = 1; i <= Math.ceil(totalDocuments / documentsPerPage); i++) {
    pageNumbers.push(i);
  }

  const paginate = (pageNumber, e) => {
    e.preventDefault();
    setCurrentPage(pageNumber);
  };

  return (
    <nav>
      <ul
        className="pagination"
        style={{
          display: "flex",
          listStyle: "none",
          gap: "8px",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          margin: 0,
          padding: 0,
        }}
      >
        {pageNumbers.map((number) => (
          <li key={number} className={`page-item ${currentPage === number ? "active" : ""}`}>
            <a onClick={(e) => paginate(number, e)} href="!#" className="page-link">
              {number}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
};

export const SelectDocuments = inject("store")(
  observer(({ store, handleOpen }: { store: Store; handleOpen: () => void }) => {
    const classes = useStyles();
    const toasterId = useId("filter_toaster");
    const { dispatchToast } = useToastController(toasterId);
    const [position] = React.useState<ToastPosition>("top");
    const [currentPage, setCurrentPage] = React.useState(1);
    const [documentsQuery, setDocumentsQuery] = React.useState<string>("");
    // Get all documents and filter by query
    if (!store.documents) {
      return <div>Loading documents...</div>;
    }
    useEffect(() => {
      async function fetchDocs() {
        if (store.filtersApplied) {
          await store.fetchFilteredDocuments();
        }
      }
      fetchDocs();
    }, [store.filters, store.filtersApplied, store.bearerToken]);
    if (!store.documents) {
      return <div>Loading documents...</div>;
    }

    const [selectedOptions, setSelectedOptions] = React.useState<string[]>([]);

    const documentsPerPage = 10;
    let filteredDocuments: string[] = [];
    try {
      let docs = toJS(store.documents) || [];

      let allDocs = Object.values(docs).flat();
      if (allDocs.length === 0) {
        return <div>Loading</div>;
      }
      filteredDocuments =
        documentsQuery.trim().length === 0
          ? allDocs
          : allDocs.filter((doc) => doc.toLowerCase().includes(documentsQuery.toLowerCase()));
    } catch (error) {
      console.error("Error filtering documents:", error);
      return <div>Error loading documents.</div>;
    }

    // Pagination logic
    const paginatedDocuments = filteredDocuments.slice(
      (currentPage - 1) * documentsPerPage,
      currentPage * documentsPerPage
    );
    console.log("paginatedDocuments", paginatedDocuments);
    // Handlers
    const handleSearchChange = (event: SearchBoxChangeEvent, data: InputOnChangeData) => {
      console.log("event ", event.target);
      setDocumentsQuery(data.value);
      setCurrentPage(1); // Reset to first page on search
    };

    const handleSelect = (doc: string) => {
      if (selectedOptions.includes(doc)) {
        setSelectedOptions(selectedOptions.filter((d) => d !== doc));
      } else if (selectedOptions.length < 10) {
        setSelectedOptions([...selectedOptions, doc]);
      }
    };

    const handleApply = () => {
      if (selectedOptions.length === 0) {
        store.setDocumentsSelected([]);
        console.log("No documents selected");

        informUser("Please select at least one document.", dispatchToast, position);
        return;
      }
      if (selectedOptions.length > 10) {
        store.setDocumentsSelected([]);
        console.error("You can only select up to 10 documents.", dispatchToast, position);
        return;
      }
      store.setDocumentsSelected([JSON.stringify(selectedOptions)]);
      informUser("You can now ask a question against the selected documents.", dispatchToast, position);
      console.log("Documents selected:", selectedOptions);
      console.log("Documents selected:", store.documentsSelected);
      store.setFiltersApplied(false);
      handleOpen();
    };

    return (
      <div className={classes.root}>
        <Toaster toasterId={toasterId} />
        <div className={classes.combobox} style={{ padding: "2px" }}>
          <h2>Filtered Documents</h2>
          <div style={{ display: "flex", flexDirection: "row", gap: "8px" }}>
            <p style={{ color: "red" }}>Important! </p>
            <p style={{ color: "black " }}>Select one to ten documents</p>
          </div>
          <Tooltip
            content={{ children: "Select one to ten documents from the list below." }}
            positioning="above-start"
            withArrow
            relationship="label"
          />
          {/* Search bar DOES NOT WORK */}

          <SearchBox
            className={classes.searchBar}
            placeholder="Search documents"
            value={documentsQuery}
            onChange={handleSearchChange}
          />
          {/* Selected tags */}
          <ul className={classes.documentList}>
            {paginatedDocuments.map((doc) => {
              const docKey = Object.keys(doc)[0];
              const docUrl = doc[docKey];
              console.log("docKey", docKey);
              const docStr = docKey.trim();
              const isSelected = selectedOptions?.includes(docStr);

              return (
                <li
                  key={docStr}
                  style={{
                    textAlign: "left",
                    marginBottom: "0.5em",
                    alignContent: "left",
                    display: "flex",
                    alignItems: "flex-start",
                    flexDirection: "row",
                  }}
                >
                  <input
                    type="checkbox"
                    value={docStr}
                    name={docStr}
                    checked={isSelected}
                    onChange={() => handleSelect(docStr)}
                  />
                  <a
                    href={docUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      marginLeft: "0.5em",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      color: "#0078d4",
                      textDecoration: "underline",
                      cursor: "pointer",
                    }}
                  >
                    {docStr}
                  </a>
                </li>
              );
            })}
          </ul>
          {/* Pagination */}
          <Pagination
            documentsPerPage={documentsPerPage}
            totalDocuments={Object.values(store.documents).flat().length}
            setCurrentPage={setCurrentPage}
            currentPage={currentPage}
          />
        </div>
        {Object.values(store.documents).flat().length === 0 && (
          <div style={{ marginTop: "1em", color: "#888" }}>Loading documents</div>
        )}
        <div className={classes.buttonArea}>
          <Button appearance="primary" onClick={handleApply}>
            Apply
          </Button>
        </div>
      </div>
    );
  })
);
